import { lazy, Suspense, useEffect, useRef } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { AppDispatch, RootState } from './store'
import { loadUser } from './store/slices/authSlice'
import ProtectedRoute from './components/auth/ProtectedRoute'
import Layout from './components/layout/Layout'
import { hasAccessToPath } from './config/menuConfig.tsx'
import { SocketProvider } from './contexts/SocketContext'

// Pages kept eager: small, or on the critical auth/landing path
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import DashboardPage from './pages/DashboardPage'
import ProfilePage from './pages/ProfilePage'
import NotFoundPage from './pages/NotFoundPage'

// Pages lazy-loaded: large and/or not on the landing path
const ProjectsPage = lazy(() => import('./pages/projects/ProjectsPage'))
const ProjectDetailPage = lazy(() => import('./pages/projects/ProjectDetailPage'))
const KanbanPage = lazy(() => import('./pages/kanban/KanbanPage'))
const IssueDetailPage = lazy(() => import('./pages/issues/IssueDetailPage'))
const MyBoardPage = lazy(() => import('./pages/kanban/MyBoardPage'))
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'))
const LeavesPage = lazy(() => import('./pages/leaves/LeavesPage'))

// Admin Pages
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))

// Loading component
import LoadingSpinner from './components/common/LoadingSpinner'

// Role-based route protection component
const RoleProtectedRoute: React.FC<{ 
  children: React.ReactNode
  path: string
}> = ({ children, path }) => {
  const { user } = useSelector((state: RootState) => state.auth)
  
  if (!user || !hasAccessToPath(path, user.role)) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

function App() {
  const dispatch = useDispatch<AppDispatch>()
  const { isAuthenticated, loading } = useSelector((state: RootState) => state.auth)
  const userLoadRef = useRef<boolean>(false)

  useEffect(() => {
    // Always try to load user on app start (only once, even in StrictMode)
    // This will either load the user if token exists, or clear the state if token is invalid
    if (userLoadRef.current) return
    userLoadRef.current = true
    dispatch(loadUser())
  }, [dispatch])

  // Show loading spinner while determining authentication state
  if (loading) {
    return <LoadingSpinner message="Loading..." />
  }

    return (
    <SocketProvider>
      <Suspense fallback={<LoadingSpinner message="Loading..." />}>
      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            !loading && isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } 
        />
        <Route 
          path="/register" 
          element={
            !loading && isAuthenticated ? <Navigate to="/dashboard" replace /> : <RegisterPage />
          } 
        />

        {/* Protected Routes - All using unified Layout */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
        <Route index element={<Navigate to="/dashboard" replace />} />
        
        {/* Main Navigation */}
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectDetailPage />} />
        <Route path="projects/:projectId/kanban" element={<KanbanPage />} />
        <Route path="projects/:projectId/kanban/issues/:issueId" element={<IssueDetailPage />} />

        <Route path="issues/:issueId" element={<IssueDetailPage />} />
        <Route path="profile" element={<ProfilePage />} />
        
        {/* SAR Vexa Management */}
        <Route 
          path="kanban" 
          element={
            <RoleProtectedRoute path="/kanban">
              <KanbanPage />
            </RoleProtectedRoute>
          } 
        />
        <Route 
          path="my-board" 
          element={
            <RoleProtectedRoute path="/my-board">
              <MyBoardPage />
            </RoleProtectedRoute>
          } 
        />
        <Route
          path="reports"
          element={
            <RoleProtectedRoute path="/reports">
              <ReportsPage />
            </RoleProtectedRoute>
          }
        />
        <Route
          path="leaves"
          element={
            <RoleProtectedRoute path="/leaves">
              <LeavesPage />
            </RoleProtectedRoute>
          }
        />

        {/* Admin Routes */}
        <Route 
          path="admin/users" 
          element={
            <RoleProtectedRoute path="/admin/users">
              <UsersPage />
            </RoleProtectedRoute>
          } 
        />
      </Route>

      {/* 404 Page */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
      </Suspense>
    </SocketProvider>
  )
}

export default App
